import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { TVerificationData, useSignUpGuestStore } from '@/features/sign-up'
import {
  PASSPORT_EMAIL_VERIFICATION_DIALOG,
  PassportEmailDialog,
  type TPassportVerificationForm,
} from '@/features/verify-identity'
import { AlertDialog, ConfirmDialog } from '@/shared/ui'

import { DIALOG } from '../../lib/sign-up-guest'
import { useVerificationDialog } from '../../lib/verification-dialogs'

type Step = 'passport' | 'email'

type Props = {
  passportDefaultValues?: Partial<TPassportVerificationForm>
  onSuccess: () => void
  email: string
}

export const VerificationDialogs = ({
  passportDefaultValues,
  onSuccess,
  email,
}: Props) => {
  const t = useTranslations()
  const tPassport = useTranslations('passport_verification')
  const tEmail = useTranslations('email_verification')

  const { actions } = useSignUpGuestStore()

  const [step, setStep] = useState<Step>('passport')

  const {
    isOpen,
    open,
    close,
    closeHandler,
    handlePhoneDialogConfirm,
    handlePassportEmailDialogConfirm,
    handlePassportAdminEmailDialogConfirm,
  } = useVerificationDialog()

  const handleClose = () => {
    open(
      step === 'passport'
        ? DIALOG.PASSPORT_EXIT_CONFIRM_DIALOG
        : DIALOG.EMAIL_EXIT_CONFIRM_DIALOG,
    )
  }

  const handleExitConfirm = (type: 'passport' | 'email') => {
    if (type === 'passport') {
      close(DIALOG.PASSPORT_EXIT_CONFIRM_DIALOG)
    } else {
      close(DIALOG.EMAIL_EXIT_CONFIRM_DIALOG)
    }

    actions.resetVerification()
    setStep('passport')
    close(PASSPORT_EMAIL_VERIFICATION_DIALOG)
  }

  const handlePassportOCRSuccess = (verification: TVerificationData) => {
    actions.updateVerification(verification)
    setStep('email')
    open(DIALOG.PASSPORT_OCR_SUCCESS_DIALOG)
  }

  const handlePassportOCRSuccessConfirm = () => {
    close(DIALOG.PASSPORT_OCR_SUCCESS_DIALOG)
  }

  const handlePassportEmailSuccess = (emailCertToken: string) => {
    actions.updateVerification({ emailCertToken })
    onSuccess()
  }

  const handlePassportMaxFail = (verification: TVerificationData) => {
    actions.updateVerification(verification)

    open(DIALOG.PASSPORT_OCR_FAIL_DIALOG)
  }

  const handlePassportOCRFailConfirm = () => {
    close(DIALOG.PASSPORT_OCR_FAIL_DIALOG)
    setStep('email')
  }

  return (
    <>
      {isOpen(PASSPORT_EMAIL_VERIFICATION_DIALOG) && (
        <PassportEmailDialog
          isOpen={isOpen(PASSPORT_EMAIL_VERIFICATION_DIALOG)}
          onClose={handleClose}
          onSuccessPassport={handlePassportOCRSuccess}
          onSuccess={handlePassportEmailSuccess}
          onMaxFail={handlePassportMaxFail}
          defaultValues={passportDefaultValues}
          email={email}
          step={step}
        />
      )}

      {/* 여권 인증 취소 확인 */}
      <ConfirmDialog
        isOpen={isOpen(DIALOG.PASSPORT_EXIT_CONFIRM_DIALOG)}
        onOpenChange={closeHandler(DIALOG.PASSPORT_EXIT_CONFIRM_DIALOG)}
        title={tPassport('dialog.exit_confirm.title')}
        content={tPassport('dialog.exit_confirm.description')}
        cancelText={tPassport('dialog.exit_confirm.button.exit')}
        confirmText={tPassport('dialog.exit_confirm.button.continue')}
        onCancel={() => {
          handleExitConfirm('passport')
        }}
        onConfirm={closeHandler(DIALOG.PASSPORT_EXIT_CONFIRM_DIALOG)}
        preventOutsideClick
      />

      {/* 이메일 인증 취소 확인 */}
      <ConfirmDialog
        isOpen={isOpen(DIALOG.EMAIL_EXIT_CONFIRM_DIALOG)}
        onOpenChange={closeHandler(DIALOG.EMAIL_EXIT_CONFIRM_DIALOG)}
        title={tEmail('dialog.exit_confirm.title')}
        content={tEmail('dialog.exit_confirm.description')}
        cancelText={tEmail('dialog.exit_confirm.button.exit')}
        confirmText={tEmail('dialog.exit_confirm.button.continue')}
        onCancel={() => {
          handleExitConfirm('email')
        }}
        onConfirm={closeHandler(DIALOG.EMAIL_EXIT_CONFIRM_DIALOG)}
        preventOutsideClick
      />

      {/* 여권 OCR 성공 - 이메일 인증 단계로 이동 */}
      <AlertDialog
        isOpen={isOpen(DIALOG.PASSPORT_OCR_SUCCESS_DIALOG)}
        onOpenChange={closeHandler(DIALOG.PASSPORT_OCR_SUCCESS_DIALOG)}
        title={tPassport('dialog.scan_completed.title')}
        content={tPassport('dialog.scan_completed.description')}
        onConfirm={handlePassportOCRSuccessConfirm}
        preventOutsideClick
      />

      {/* 여권 OCR 3회 실패 - 관리자 여권 심사 진행 안내 */}
      <AlertDialog
        isOpen={isOpen(DIALOG.PASSPORT_OCR_FAIL_DIALOG)}
        onOpenChange={closeHandler(DIALOG.PASSPORT_OCR_FAIL_DIALOG)}
        title={tPassport('dialog.scan_failed_admin_review.title')}
        content={tPassport('dialog.scan_failed_admin_review.description')}
        onConfirm={handlePassportOCRFailConfirm}
        preventOutsideClick
      />

      {/* 휴대폰 본인인증 완료 */}
      <AlertDialog
        isOpen={isOpen(DIALOG.PHONE_SUCCESS_DIALOG)}
        onOpenChange={closeHandler(DIALOG.PHONE_SUCCESS_DIALOG)}
        title={t('account.dialog.identity_verification_complete.title')}
        content={t('account.dialog.identity_verification_complete.description')}
        onConfirm={handlePhoneDialogConfirm}
        preventOutsideClick
      />

      {/* 여권 및 이메일 인증 완료 */}
      <AlertDialog
        isOpen={isOpen(DIALOG.PASSPORT_SUCCESS_DIALOG)}
        onOpenChange={closeHandler(DIALOG.PASSPORT_SUCCESS_DIALOG)}
        title={t('email_verification.dialog.completed.title')}
        content={t('email_verification.dialog.completed.description')}
        onConfirm={handlePassportEmailDialogConfirm}
        preventOutsideClick
      />

      {/* 여권 수동 심사 및 이메일 인증 완료 */}
      <AlertDialog
        isOpen={isOpen(DIALOG.PASSPORT_SUCCESS_ADMIN_DIALOG)}
        onOpenChange={closeHandler(DIALOG.PASSPORT_SUCCESS_ADMIN_DIALOG)}
        title={tPassport('dialog.signup_completed.title')}
        content={tPassport('dialog.signup_completed.admin_review.description')}
        onConfirm={handlePassportAdminEmailDialogConfirm}
        preventOutsideClick
      />
    </>
  )
}
